import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, SparkModalPortal, Text, sparkToast } from '@spark';
import { createModelPerfRun, getModelPerfPlatform } from '../../api/modelPerfRunsApi';

const PRESETS = [
  { id: 'quick', label: 'Quick', concurrency: [1], minutes: 1 },
  { id: 'typical', label: 'Typical', concurrency: [1, 5], minutes: 3 },
  { id: 'stress', label: 'Stress', concurrency: [50], minutes: 5 },
];

export default function RunBenchmarkModal({
  open,
  onClose,
  clientId,
  llmUsecaseId,
  modelVersion,
  taskType,
  onQueued,
}) {
  const [preset, setPreset] = useState('typical');
  const [confirm, setConfirm] = useState(false);
  const [pending, setPending] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [platformSku, setPlatformSku] = useState(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    let cancelled = false;
    getModelPerfPlatform((data) => {
      if (!cancelled && data?.cloud_provider && data?.instance_type) {
        setPlatformSku({
          cloudProvider: data.cloud_provider,
          instanceType: data.instance_type,
          accelerator: data.accelerator || null,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const env = platformSku;
  const selected = useMemo(() => PRESETS.find((item) => item.id === preset) || PRESETS[1], [preset]);
  const isSummarization = String(taskType || '').toLowerCase() === 'summarization';

  const submit = () => {
    if (!clientId || !llmUsecaseId || !modelVersion) {
      sparkToast.error('Select a trained version before running a serving-perf benchmark.');
      return;
    }
    if (!env?.cloudProvider || !env?.instanceType) {
      sparkToast.error('Could not resolve this platform’s serving-perf SKU.');
      return;
    }
    setPending(true);
    createModelPerfRun(
      {
        client_id: clientId,
        llm_usecase_id: llmUsecaseId,
        model_version: String(modelVersion),
        cloud_provider: env.cloudProvider,
        instance_type: env.instanceType,
        accelerator: env.accelerator,
        preset,
        concurrency_levels: selected.concurrency,
        budget: { mode: 'time', value: selected.minutes },
        confirm,
      },
      (data) => {
        setPending(false);
        if (data?.requires_confirm) {
          setEstimate(data);
          setConfirm(true);
          sparkToast.info(`This run is estimated at $${data.estimated_cost_usd}. Confirm to start.`);
          return;
        }
        sparkToast.success('Serving-perf run queued');
        onQueued?.(data);
        onClose();
        setConfirm(false);
        setEstimate(null);
      },
      (error) => {
        setPending(false);
        sparkToast.error(error || 'Failed to start serving-perf run');
      },
    );
  };

  return (
    <SparkModalPortal open={open} onClose={onClose} size="slot">
      <Modal
        title="Run serving-perf benchmark"
        subtitle="Isolated replica. Seeded catalog stays the baseline."
        showSubtitle
        primaryLabel={pending ? 'Starting…' : confirm ? 'Confirm run' : 'Run benchmark'}
        tertiaryLabel="Cancel"
        primaryDisabled={pending || isSummarization || !modelVersion || !env}
        primaryDisableWhenReadOnly
        onCloseClick={onClose}
        onTertiaryClick={onClose}
        onPrimaryClick={submit}
        fixedHeight={false}
      >
        <Text variant="base-regular" color="secondary">
          Measures this trained version on an isolated replica. Live inference is
          not deployed or changed.
        </Text>
        {isSummarization && (
          <Text variant="base-regular" style={{ marginTop: 12 }}>
            Summarization (vLLM) is not enabled on this replica yet. Encoder tasks only.
          </Text>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          {PRESETS.map((item) => (
            <Button
              key={item.id}
              size="small"
              variant={preset === item.id ? 'primary' : 'secondary'}
              onClick={() => setPreset(item.id)}
            >
              {item.label}
            </Button>
          ))}
        </div>
        <Text variant="base-regular" style={{ marginTop: 16 }}>
          {env
            ? `This platform: ${env.cloudProvider} / ${env.instanceType}${
                env.accelerator ? ` (${env.accelerator})` : ''
              }`
            : 'Resolving this platform’s SKU…'}
        </Text>
        <Text variant="base-regular" color="secondary">
          Concurrency {selected.concurrency.join(', ')} · {selected.minutes} min
        </Text>
        {estimate && (
          <Text variant="base-regular" style={{ marginTop: 12 }}>
            Estimate ${estimate.estimated_cost_usd} over {estimate.estimated_duration_minutes} min.
            Click Run again to confirm.
          </Text>
        )}
      </Modal>
    </SparkModalPortal>
  );
}
